import { isOrgWideRole, type ApplyAccess, type Member } from '@headline/schemas'
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { InvitationsSection } from '@/components/access/InvitationsSection'
import { MembersSection } from '@/components/access/MembersSection'
import {
  AccessPickerDialog,
  type AccessPickerApply,
} from '@/components/accounts/AccessPickerDialog'
import { visibleTreeResources } from '@/lib/access'
import {
  assignableRolesQuery,
  invitationsQuery,
  membersQuery,
  orgAccessOverviewQuery,
  useApplyAccessBatch,
} from '@/lib/queries'

/**
 * The Users tab — who is in this organization, and who's been offered a place in it.
 *
 * "Create a user" here is an invitation with a role attached: WorkOS emails the invite and
 * hosts the accept flow, acceptance creates the membership, and membership is what admits a
 * session at all (the API rejects membership-less sessions). Roles load too because the
 * invite form and each member row carry a role picker.
 *
 * Each member row also opens the permission picker, person-first across every account — the
 * org-wide answer to "what can this person reach?" that no single account page can give. The
 * org-wide role and the picker deliberately stay separate controls: the role is coarse platform
 * power carried in the session token, the matrix is resource access carried in FGA, and merging
 * them into one grid would blur exactly the boundary that makes delegation safe.
 *
 * Time-boxed access used to be a third section here, and isn't any more: a grant is nearly
 * always "access to THIS account until Friday", and issuing it from a global screen meant
 * choosing the account from a dropdown of every customer, then coming back here to see whether
 * it was still in force. It lives on the granting itself now, as the picker's duration field.
 */
export const Route = createFileRoute('/app/access/users')({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(membersQuery),
      context.queryClient.ensureQueryData(invitationsQuery),
      context.queryClient.ensureQueryData(assignableRolesQuery),
    ]),
  component: UsersTab,
})

function UsersTab() {
  const queryClient = useQueryClient()
  const { data: membersData } = useSuspenseQuery(membersQuery)
  const { data: invitationsData } = useSuspenseQuery(invitationsQuery)
  const { data: rolesData } = useSuspenseQuery(assignableRolesQuery)
  // Deliberately not in the loader: it costs one WorkOS listing per registered resource, and a
  // failure (WorkOS unconfigured, say) must degrade to "no Edit access buttons" rather than
  // taking the whole tab down with it.
  const overview = useQuery({ ...orgAccessOverviewQuery, retry: false })
  const applyBatch = useApplyAccessBatch(queryClient)
  const [editing, setEditing] = useState<Member | null>(null)

  // Org-wide roles only. Resource-scoped roles (account-viewer, subaccount-editor, …) grant
  // NOTHING when attached to a membership org-wide — offering them here is offering a no-op,
  // and roleSchema's own docblock warns it's the first option people click. They're granted
  // through the picker instead, as capabilities.
  const orgRoles = rolesData.roles.flatMap((role) =>
    isOrgWideRole(role) ? [{ slug: role.slug, name: role.name }] : [],
  )

  const trees =
    overview.data?.trees.map((tree) =>
      visibleTreeResources(tree.resources, overview.data.people, overview.data.pending),
    ) ?? []

  // A member with no grants anywhere still opens the picker — with an empty matrix, which is
  // the honest answer to "what can they reach?".
  const editingPerson = editing
    ? (overview.data?.people.find((p) => p.membershipId === editing.membershipId) ?? {
        membershipId: editing.membershipId,
        email: editing.email,
        name: editing.name,
        holdings: [],
      })
    : null

  // The apply endpoint is rooted at one account tree, so a diff spanning accounts becomes one
  // apply per touched account. Every entry maps to its tree's root; the picker only shows rows
  // from these trees, so the lookup can't miss.
  const applyAcross = (input: AccessPickerApply, done: () => void) => {
    const rootOf = new Map<string, string>()
    for (const tree of trees) {
      const root = tree[0]
      if (!root) continue
      for (const resource of tree) rootOf.set(resource.resourceId, root.resourceId)
    }
    const byRoot = new Map<string, ApplyAccess>()
    const bucket = (accountId: string): ApplyAccess => {
      const existing = byRoot.get(accountId)
      if (existing) return existing
      const created: ApplyAccess = {
        accountId,
        email: input.email,
        grants: [],
        revokes: [],
        ...(input.expiresAt !== undefined && { expiresAt: input.expiresAt }),
      }
      byRoot.set(accountId, created)
      return created
    }
    for (const entry of input.grants) {
      const root = rootOf.get(entry.resourceId)
      if (root) bucket(root).grants.push(entry)
    }
    for (const entry of input.revokes) {
      const root = rootOf.get(entry.resourceId)
      if (root) bucket(root).revokes.push(entry)
    }
    applyBatch.mutate([...byRoot.values()], { onSuccess: done })
  }

  return (
    <div className="flex flex-col gap-8">
      <InvitationsSection invitations={invitationsData.invitations} roles={orgRoles} />
      <MembersSection
        members={membersData.members}
        roles={orgRoles}
        {...(overview.data !== undefined && { onEditAccess: setEditing })}
      />

      {editing && editingPerson && overview.data && (
        <AccessPickerDialog
          trees={trees}
          person={editingPerson}
          people={overview.data.people}
          memberEmails={[]}
          busy={applyBatch.isPending}
          error={applyBatch.isError ? applyBatch.error.message : null}
          // Rows start at the accounts they already hold something on; the rest wait behind the
          // "Show account" dropdown, so the matrix shows what's relevant instead of every
          // customer in the organization.
          treePicker
          onApply={applyAcross}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}
